import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  Calendar,
  Clock,
  MapPin,
  User,
  ArrowRight,
  Star,
  Sparkles,
} from 'lucide-react';
import { paymentApi } from '../../services/paymentService';
import {
  formatDisplayMoney,
  paymentCatalogAmount,
} from '../../utils/formatMoney';

const money = (n) => formatDisplayMoney(n, 'USD', 'en-US');

const ClientBookingSuccess = () => {
  const location = useLocation();
  const bookingData = location.state?.bookingData || {};
  const serviceData = location.state?.serviceData || {};
  const vendorData = location.state?.vendorData || {};

  const { data: paymentsResponse } = useQuery({
    queryKey: ['payments', bookingData.id],
    queryFn: () => paymentApi.getPaymentByBooking(bookingData.id),
    enabled: !!bookingData.id,
    select: (response) => response?.data || [],
  });

  const payments = paymentsResponse || [];
  const completedPayments = payments.filter((p) => p.status === 'completed');

  const depositPayment = completedPayments.find((p) => p.payment_type === 'deposit');
  const depositAmount = depositPayment ? paymentCatalogAmount(depositPayment) : 0;

  const fullPayment = completedPayments.find((p) => p.payment_type === 'full_payment');
  const hasFullPayment = !!fullPayment;
  const hasDepositOnly = depositAmount > 0 && !hasFullPayment;
  const paidNow = hasFullPayment
    ? paymentCatalogAmount(fullPayment)
    : depositAmount;

  const packagePrice = Number(bookingData.selectedPackage?.price || 0);
  const totalAmount = Number(
    bookingData.totalAmount ||
      (Number(bookingData.subtotal_amount || 0) + Number(bookingData.platform_fee || 0)) ||
      0
  );

  const eventDateLabel = bookingData.eventDate
    ? new Date(bookingData.eventDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'Not specified';

  if (!bookingData.id) {
    return (
      <div className="min-h-screen bg-[#f7f8fa] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-zinc-300 bg-white">
            <Check className="h-6 w-6 text-zinc-400" strokeWidth={2.5} />
          </div>
          <h1
            className="text-2xl font-semibold tracking-tight text-zinc-900 mb-3"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            No booking found
          </h1>
          <p className="text-sm text-zinc-500 mb-8 leading-relaxed">
            It looks like you don’t have a valid booking to view.
          </p>
          <Link
            to="/"
            className="inline-flex items-center justify-center px-5 py-2.5 bg-zinc-900 text-white text-sm font-medium rounded-lg hover:bg-zinc-800 transition-colors"
          >
            Go home
          </Link>
        </div>
      </div>
    );
  }

  const reference =
    bookingData.booking_reference ||
    bookingData.bookingReference ||
    String(bookingData.id).slice(0, 8).toUpperCase();

  return (
    <div className="min-h-screen bg-[#f7f8fa] relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px]"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% -10%, rgba(24, 24, 27, 0.08), transparent 70%), linear-gradient(180deg, #e8eaee 0%, #f7f8fa 100%)',
        }}
      />

      <div className="page-container relative py-10 sm:py-14">
        {/* Hero confirmation */}
        <div className="text-center mb-10 sm:mb-12">
          <div className="relative inline-flex mb-6">
            <span
              aria-hidden
              className="absolute inset-0 rounded-full bg-zinc-900/10 animate-ping opacity-40"
              style={{ animationDuration: '1.8s' }}
            />
            <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-zinc-900 text-white shadow-lg shadow-zinc-900/20">
              <Check className="h-7 w-7" strokeWidth={2.5} />
            </div>
          </div>
          <p className="text-[11px] font-semibold tracking-[0.22em] uppercase text-zinc-500 mb-3">
            You’re all set
          </p>
          <h1
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900 mb-3"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Booking confirmed
          </h1>
          <p className="text-sm sm:text-base text-zinc-500 max-w-lg mx-auto leading-relaxed">
            A confirmation email is on the way. Your vendor will follow up within 24 hours.
          </p>
          <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/80 px-4 py-1.5 text-xs text-zinc-600 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5 text-zinc-400" />
            Ref <span className="font-semibold text-zinc-900 tracking-wide">{reference}</span>
          </div>
        </div>

        <div className="mx-auto max-w-5xl grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Main column */}
          <div className="lg:col-span-3 space-y-5">
            <section className="rounded-2xl border border-zinc-200/80 bg-white p-6 sm:p-7 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <h2
                className="text-lg font-semibold text-zinc-900 mb-5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Event details
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Detail
                  icon={Calendar}
                  label="Date"
                  value={eventDateLabel}
                />
                <Detail
                  icon={Clock}
                  label="Time"
                  value={bookingData.eventTime || 'Not specified'}
                />
                <Detail
                  icon={MapPin}
                  label="Location"
                  value={bookingData.location || 'Not specified'}
                  className="sm:col-span-2"
                />
              </div>
            </section>

            <section className="rounded-2xl border border-zinc-200/80 bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <div className="px-6 sm:px-7 py-5 border-b border-zinc-100 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-zinc-400 mb-1">
                    Package
                  </p>
                  <h3
                    className="text-base font-semibold text-zinc-900"
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {bookingData.selectedPackage?.name || 'Service package'}
                  </h3>
                  {bookingData.selectedPackage?.description && (
                    <p className="mt-1.5 text-sm text-zinc-500 leading-relaxed line-clamp-2">
                      {bookingData.selectedPackage.description}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-semibold tabular-nums text-zinc-900">
                    {money(packagePrice)}
                  </p>
                  <p className="text-xs text-zinc-400 mt-0.5">
                    {bookingData.duration ? `${bookingData.duration} hours` : 'Duration TBD'}
                  </p>
                </div>
              </div>

              <div className="px-6 sm:px-7 py-5 space-y-3">
                <div className="flex justify-between items-baseline gap-3">
                  <span className="text-sm text-zinc-500">Order total</span>
                  <span className="text-base font-semibold tabular-nums text-zinc-900">
                    {money(totalAmount)}
                  </span>
                </div>
                {(hasFullPayment || hasDepositOnly) && (
                  <div className="flex justify-between items-baseline gap-3">
                    <span className="text-sm text-zinc-500">
                      {hasFullPayment ? 'Paid in full' : 'Deposit paid'}
                    </span>
                    <span className="text-sm font-medium tabular-nums text-emerald-700">
                      {money(paidNow)}
                    </span>
                  </div>
                )}
                <div className="mt-2 rounded-xl bg-zinc-900 px-4 py-3.5 flex justify-between items-center gap-3">
                  <span className="text-sm font-medium text-zinc-300">
                    {hasFullPayment ? 'Amount paid' : 'Paid today'}
                  </span>
                  <span className="text-xl font-semibold tabular-nums text-white tracking-tight">
                    {money(paidNow || totalAmount)}
                  </span>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-zinc-200/80 bg-white p-6 sm:p-7 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <h2
                className="text-lg font-semibold text-zinc-900 mb-5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                What’s next
              </h2>
              <ol className="space-y-4">
                {[
                  {
                    title: 'Check your inbox',
                    body: 'Confirmation with booking details lands shortly.',
                  },
                  {
                    title: 'Vendor reaches out',
                    body: 'Expect contact within 24 hours to lock in the plan.',
                  },
                  {
                    title: 'Enjoy the event',
                    body: 'Show up ready — we’ll handle the rest with your vendor.',
                  },
                ].map((step, i) => (
                  <li key={step.title} className="flex gap-3.5">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-[11px] font-semibold text-zinc-700">
                      {i + 1}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{step.title}</p>
                      <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-2 space-y-5">
            <section className="rounded-2xl border border-zinc-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-zinc-400 mb-4">
                Your vendor
              </p>
              <div className="flex items-center gap-3 mb-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-zinc-900 text-white">
                  <User className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-zinc-900 truncate">
                    {vendorData.business_name || 'Vendor'}
                  </h3>
                  <div className="flex items-center text-xs text-zinc-500 mt-0.5">
                    <Star className="h-3 w-3 text-zinc-800 fill-zinc-800 mr-1" />
                    {Number(serviceData.average_rating || 0).toFixed(1)}
                    <span className="text-zinc-400 ml-1">
                      ({serviceData.total_reviews || 0})
                    </span>
                  </div>
                </div>
              </div>
              {(vendorData.city || vendorData.state) && (
                <div className="flex items-center text-xs text-zinc-500 gap-2">
                  <MapPin className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    {[vendorData.city, vendorData.state].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </section>

            <section className="rounded-2xl border border-zinc-200/80 bg-white p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
              <p className="text-[11px] font-semibold tracking-[0.16em] uppercase text-zinc-400 mb-4">
                Service
              </p>
              <dl className="space-y-3.5 text-sm">
                <Row label="Name" value={serviceData.service_name || '—'} />
                <Row label="Category" value={serviceData.category_name || '—'} />
                <Row
                  label="Duration"
                  value={bookingData.duration ? `${bookingData.duration} hours` : '—'}
                />
                <Row
                  label="Guests"
                  value={bookingData.guestCount || 'Not specified'}
                />
              </dl>
            </section>

            <div className="flex flex-col gap-2.5 pt-1">
              <Link
                to="/bookings"
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-sm font-medium text-zinc-900 hover:bg-zinc-50 transition-colors"
              >
                <Calendar className="h-4 w-4" />
                View my bookings
              </Link>
              <Link
                to="/browse"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-800 transition-colors"
              >
                Browse more services
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function Detail({ icon: Icon, label, value, className = '' }) {
  return (
    <div className={`flex gap-3 ${className}`}>
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700">
        <Icon className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold tracking-wide uppercase text-zinc-400 mb-0.5">
          {label}
        </p>
        <p className="text-sm text-zinc-800 leading-snug">{value}</p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 items-baseline">
      <dt className="text-zinc-500">{label}</dt>
      <dd className="text-zinc-900 font-medium text-right truncate">{value}</dd>
    </div>
  );
}

export default ClientBookingSuccess;
